import { useEffect, useState } from "react";
import { ArrowUpRight, Check, CreditCard, Landmark } from "lucide-react";
import { toast } from "sonner";
import { useLanguage, fmtEUR } from "@/i18n";
import { api } from "@/api";
import { Reveal, Overline } from "@/components/motion";

const Membership = () => {
  const { lang, t } = useLanguage();
  const [plan, setPlan] = useState("yearly");
  const [method, setMethod] = useState("card");
  const [form, setForm] = useState({ name: "", email: "" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [bank, setBank] = useState(null);

  useEffect(() => {
    api.get("/config/public").then((r) => setBank(r.data.bank)).catch(() => {});
  }, []);

  const benefits = t("membership.benefits");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (method === "card") {
        const { data } = await api.post("/payments/checkout", {
          lookup_key: plan === "monthly" ? "membership_monthly" : "membership_yearly",
          origin_url: window.location.origin,
          name: form.name,
          email: form.email,
        });
        window.location.href = data.checkout_url;
      } else {
        const { data } = await api.post("/members/apply", { name: form.name, email: form.email, plan });
        setResult(data);
        toast.success(t("membership.successTitle"));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
      setLoading(false);
    }
  };

  const PlanCard = ({ id, data }) => (
    <button
      type="button"
      data-testid={`plan-${id}`}
      onClick={() => setPlan(id)}
      className={`flex-1 min-w-[220px] text-left border p-8 transition-transform duration-300 hover:-translate-y-1 ${plan === id ? "border-clay bg-clay/5 ring-1 ring-clay" : "border-forest/15 bg-cream"}`}
    >
      <p className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">{data.name}</p>
      <p className="mt-3 font-serif text-5xl font-semibold text-forest">
        {data.price}
        <span className="text-lg font-normal text-sage">{data.period}</span>
      </p>
      <p className="mt-3 text-sm text-sage">{data.note}</p>
    </button>
  );

  return (
    <div data-testid="membership-page" className="pt-24">
      <section className="max-w-[1500px] mx-auto px-6 md:px-12 pt-16 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-16">
        <div className="lg:col-span-6">
          <Reveal>
            <Overline>{t("membership.overline")}</Overline>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter text-forest" data-testid="membership-title">
              {t("membership.title")}
            </h1>
            <p className="mt-8 text-base lg:text-lg leading-relaxed text-sage max-w-xl">{t("membership.sub")}</p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-14 border border-forest/15 p-8 bg-cream" data-testid="membership-benefits">
              <Overline>{t("membership.benefitsTitle")}</Overline>
              <ul className="flex flex-col gap-4">
                {benefits.map((b, i) => (
                  <li key={i} className="flex items-start gap-3 text-forest" data-testid={`benefit-${i}`}>
                    <Check size={18} className="text-clay mt-0.5 shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-6">
          <Reveal delay={0.2}>
            {!result ? (
              <form onSubmit={submit} className="border border-forest/15 bg-sand/50 p-8 md:p-12" data-testid="membership-form">
                <Overline>{t("membership.choosePlan")}</Overline>
                <div className="flex flex-wrap gap-4 mb-10">
                  <PlanCard id="monthly" data={t("membership.monthly")} />
                  <PlanCard id="yearly" data={t("membership.yearly")} />
                </div>

                <Overline>{t("membership.details")}</Overline>
                <div className="flex flex-col gap-4 mb-10">
                  <input
                    data-testid="member-name-input"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder={t("membership.name")}
                    className="w-full bg-cream border border-forest/20 rounded-full px-5 py-3.5 text-sm text-forest placeholder:text-sage/60 focus:outline-none focus:border-clay transition-colors duration-300"
                  />
                  <input
                    data-testid="member-email-input"
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder={t("membership.email")}
                    className="w-full bg-cream border border-forest/20 rounded-full px-5 py-3.5 text-sm text-forest placeholder:text-sage/60 focus:outline-none focus:border-clay transition-colors duration-300"
                  />
                </div>

                <Overline>{t("membership.method")}</Overline>
                <div className="flex flex-col gap-3 mb-10">
                  <button
                    type="button"
                    data-testid="method-card"
                    onClick={() => setMethod("card")}
                    className={`flex items-center gap-3 border px-6 py-4 text-sm font-medium text-left transition-colors duration-300 ${method === "card" ? "border-clay bg-clay/5 text-forest" : "border-forest/15 text-sage hover:border-forest/30"}`}
                  >
                    <CreditCard size={18} className="text-clay" /> {t("membership.card")}
                  </button>
                  <button
                    type="button"
                    data-testid="method-bank"
                    onClick={() => setMethod("bank")}
                    className={`flex items-center gap-3 border px-6 py-4 text-sm font-medium text-left transition-colors duration-300 ${method === "bank" ? "border-clay bg-clay/5 text-forest" : "border-forest/15 text-sage hover:border-forest/30"}`}
                  >
                    <Landmark size={18} className="text-clay" /> {t("membership.bank")}
                  </button>
                </div>

                <button
                  data-testid="membership-submit"
                  disabled={loading}
                  className="w-full inline-flex items-center justify-center gap-2 bg-clay text-cream px-8 py-4 rounded-full font-medium hover:bg-clay-hover hover:-translate-y-0.5 transition-transform duration-300 disabled:opacity-60"
                >
                  {method === "card" ? t("membership.submitCard") : t("membership.submitBank")} <ArrowUpRight size={18} />
                </button>
              </form>
            ) : (
              <div className="border border-moss/30 bg-moss/5 p-8 md:p-12" data-testid="membership-success">
                <h2 className="font-serif text-4xl text-forest mb-4">{t("membership.successTitle")}</h2>
                <p className="text-sage leading-relaxed mb-8">{t("membership.successText")}</p>
                {bank && (
                  <div className="flex flex-col gap-2 text-sm text-sage border-t border-moss/20 pt-6">
                    <p><span className="font-semibold text-forest">{t("detail.reference")}:</span> <span data-testid="membership-reference" className="font-mono text-clay">{result.reference}</span></p>
                    <p><span className="font-semibold text-forest">{t("detail.iban")}:</span> {bank.iban}</p>
                    <p><span className="font-semibold text-forest">{t("detail.bic")}:</span> {bank.bic}</p>
                    <p><span className="font-semibold text-forest">{t("detail.accountName")}:</span> {bank.account_name}</p>
                    <p><span className="font-semibold text-forest">{t("detail.amount")}:</span> {fmtEUR(result.amount, lang)} ({result.plan})</p>
                  </div>
                )}
              </div>
            )}
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Membership;
